import React, { useCallback, useEffect, useRef, useState } from 'react';
import { browserHistory } from 'react-router';

import { ownerResearchApi } from '../../requests/ownerResearch';
import { formatClock, formatNumber, formatRange, formatRunTime, zoneAbbreviation } from '../ListingSources/format';
import { Chevron, ModePill, StatusPill } from '../ListingSources/Pills';
import { Menu } from './Menu';
import ReviewWindow from './ReviewWindow';
import RunDetail from './RunDetail';
import SettingsModal from './SettingsModal';
import { CountChips, OUTCOMES, OutcomePill, WaitingStrip } from './vocabulary';

// Only a run in progress moves; the status read is the heaviest on the screen.
const POLL_MS = 10000;

// The same estimate the settings dialog shows: address, pin and contacts.
const REQUESTS_PER_PROPERTY = 3;

const PROGRESS_SEGMENTS = 30;

// A property id is digits; one digit matches half the table.
const SEARCH_MIN_LENGTH = 3;

// Whose reviews the list shows, as the URL carries it: the signed-in agent's
// own territories unless it says otherwise, "all", an agent and one of their
// territories, or the territory and market ids the property list handed over.
const MINE = 'me';
const ALL = 'all';
const FROM_LIST = 'list';
const PAIR = ':';

const pairKey = (agentId, territoryId) => agentId + PAIR + territoryId;

function filterFromQuery(query) {
    const agent = query && query.agent !== undefined ? String(query.agent) : MINE;
    return {
        agent: agent === ALL || agent === '' ? null : agent,
        territory: query && query.territory ? String(query.territory) : null,
        market: query && query.territory_market ? String(query.territory_market) : null,
    };
}

function sameFilter(a, b) {
    return a.agent === b.agent && a.territory === b.territory && a.market === b.market;
}

function filterParams(filter) {
    const params = {};
    if (filter.agent) params.agent = filter.agent;
    if (filter.territory) params.territory = filter.territory;
    if (filter.market) params.territory_market = filter.market;
    return params;
}

function scheduleLine(status) {
    const settings = status.settings;
    const excluded = settings.excluded_statuses || [];
    const included = (status.statuses || [])
        .filter((row) => !excluded.some((each) => (each || null) === (row.status || null)))
        .length;
    const parts = [
        settings.enabled
            ? 'Nightly ' + settings.run_at + ' ' + zoneAbbreviation(settings.timezone)
            : 'No nightly run',
        'properties with no owner or Need Owner ticked',
        formatNumber(included) + (included === 1 ? ' status' : ' statuses'),
        settings.daily_requests > 0
            ? formatNumber(settings.daily_requests) + ' LightBox requests a day'
            : 'no daily limit',
        settings.check_first === 'active' ? 'Active first' : 'Newest first',
        settings.ai_enabled ? 'AI on' : 'AI off',
    ];

    return parts.join(' · ');
}

function Backlog({ status }) {
    const { checked, remaining } = status.backlog;
    const total = checked + remaining;
    const fraction = total ? checked / total : 0;
    const limit = status.settings.daily_requests;
    const nights = limit > 0 ? Math.ceil((remaining * REQUESTS_PER_PROPERTY) / limit) : null;

    return (
        <div className="ls-progress">
            <div className="ls-progress__head">
                <span className="ls-progress__read">{formatNumber(checked)}</span>
                <span className="ls-progress__scale">of {formatNumber(total)} properties checked so far</span>
                <span className="ls-progress__percent">{Math.round(fraction * 100)}%</span>
                {nights !== null && remaining > 0 && (
                    <span className="ls-progress__eta">~{formatNumber(nights)} nights left at {formatNumber(limit)} requests a day</span>
                )}
            </div>
            <div className="ls-progress__pages">
                {Array.apply(null, { length: PROGRESS_SEGMENTS }).map((ignored, index) => {
                    const fill = Math.max(0, Math.min(1, fraction * PROGRESS_SEGMENTS - index));
                    return (
                        <div key={index} className="ls-progress__page">
                            <div className={'ls-progress__fill' + (fill === 1 ? ' is-full' : '')} style={{ width: Math.round(fill * 100) + '%' }} />
                        </div>
                    );
                })}
            </div>
            <div className="ls-progress__foot">
                <span className="ls-progress__pagelabel">
                    {status.settings.check_first === 'active' ? 'Active properties first, then the rest' : 'Newest properties first'}
                </span>
                {status.running && <span className="ls-progress__live" />}
                <span className="ls-progress__fresh">
                    LightBox today: {formatNumber(status.usage.today)}
                    {limit > 0 ? ' of ' + formatNumber(limit) : ''} requests
                </span>
            </div>
        </div>
    );
}

/**
 * The owner research screen: the properties waiting for a person, and behind
 * one button the run's state, the backlog, the settings and every run's
 * history. Styled with the listing sources design, as the true owners screen is.
 */
export default function OwnerResearch({ location }) {
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [queueing, setQueueing] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    // The client marked the review list as the screen; the bot's controls are kept one click away.
    const [showRuns, setShowRuns] = useState(false);
    const [openRun, setOpenRun] = useState(null);
    const [openReview, setOpenReview] = useState(null);
    // Bumped after a review is closed, so an open run detail lists it afresh.
    const [detailVersion, setDetailVersion] = useState(0);

    const [runs, setRuns] = useState([]);
    const [runsMeta, setRunsMeta] = useState(null);
    const [runsPage, setRunsPage] = useState(1);

    const [reviews, setReviews] = useState([]);
    const [reviewsMeta, setReviewsMeta] = useState(null);
    const [reviewsPage, setReviewsPage] = useState(1);
    const [reviewFilter, setReviewFilter] = useState(() => filterFromQuery(location && location.query));
    // The filter the server applied, with the agent and territories named: "me" comes back as the member, or as nobody.
    const [appliedFilter, setAppliedFilter] = useState(null);
    // Only the latest list asked for lands: a slower earlier answer would show another filter's rows.
    const reviewsRequest = useRef(0);

    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    // A section that failed to load says so instead of looking empty.
    const [sectionError, setSectionError] = useState({});
    // Bumped by a failed refresh, so polling carries on past it.
    const [missedPolls, setMissedPolls] = useState(0);
    const unmounted = useRef(false);
    const loaded = useRef(false);

    const failSection = (section, failure, fallback) => !unmounted.current
        && setSectionError((was) => ({ ...was, [section]: failure.message || fallback }));
    const clearSection = (section) => setSectionError((was) => ({ ...was, [section]: null }));

    const load = useCallback(() => ownerResearchApi.status().then(
        (data) => {
            if (unmounted.current) return;
            loaded.current = true;
            setStatus(data);
            setNotice((was) => (was && was.refresh ? null : was));
        },
        (failure) => {
            if (unmounted.current) return;
            const message = failure.message || 'The owner research screen could not be loaded.';

            if (!loaded.current) {
                setError(message);
                return;
            }

            setNotice({ kind: 'error', text: 'Could not refresh: ' + message, refresh: true });
            setMissedPolls((count) => count + 1);
        },
    ), []);

    const loadRuns = useCallback((wanted) => ownerResearchApi.runs(wanted).then((envelope) => {
        if (unmounted.current) return;
        clearSection('runs');
        setRuns(envelope.data || []);
        setRunsMeta(envelope.meta ? envelope.meta.pagination : null);
        setRunsPage(wanted);
    }, (failure) => failSection('runs', failure, 'The runs could not be loaded.')), []);

    const loadReviews = useCallback((wanted) => {
        const request = ++reviewsRequest.current;

        return ownerResearchApi.reviews(wanted, filterParams(reviewFilter)).then((envelope) => {
            if (unmounted.current || request !== reviewsRequest.current) return;
            clearSection('reviews');
            setReviews(envelope.data || []);
            setReviewsMeta(envelope.meta ? envelope.meta.pagination : null);
            setAppliedFilter(envelope.meta ? envelope.meta.filter || null : null);
            setReviewsPage(wanted);
        }, (failure) => request === reviewsRequest.current && failSection('reviews', failure, 'The properties waiting for a person could not be loaded.'));
    }, [reviewFilter]);

    useEffect(() => {
        unmounted.current = false;
        load();
        loadRuns(1);
        return () => {
            unmounted.current = true;
        };
    }, [load, loadRuns]);

    // The page stays mounted when only its URL changes (the sidebar's link, a bookmark), so the filter follows the URL.
    const search = location ? location.search : '';
    useEffect(() => {
        const next = filterFromQuery(location && location.query);
        setReviewFilter((was) => (sameFilter(was, next) ? was : next));
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    // Another filter is another list, from its first page.
    useEffect(() => {
        loadReviews(1);
    }, [loadReviews]);

    const pickReviewFilter = (key) => {
        if (key === FROM_LIST) return;
        const [agent, territory] = key.split(PAIR);
        // Kept in the URL, so a reload or a shared link shows the same list; the filter follows it from there.
        browserHistory.replace({ pathname: '/owner-research', query: key === MINE ? {} : territory ? { agent, territory } : { agent } });
    };

    useEffect(() => {
        if (!status || !status.running) return undefined;
        const timer = setTimeout(() => {
            load();
            loadRuns(runsPage);
            loadReviews(reviewsPage);
        }, POLL_MS);
        return () => clearTimeout(timer);
    }, [status, missedPolls, load, loadRuns, loadReviews, runsPage, reviewsPage]);

    useEffect(() => {
        const wanted = query.trim();

        if (!/^\d+$/.test(wanted) || wanted.length < SEARCH_MIN_LENGTH) {
            setResults(null);
            return undefined;
        }

        // Typed a digit at a time, so the request waits for a pause.
        const timer = setTimeout(() => {
            ownerResearchApi.search(wanted).then(
                (data) => {
                    if (unmounted.current) return;
                    clearSection('search');
                    setResults(data.results);
                },
                (failure) => {
                    if (unmounted.current) return;
                    setResults(null);
                    failSection('search', failure, 'The search could not be run.');
                },
            );
        }, 350);
        return () => clearTimeout(timer);
    }, [query]);

    const runNow = (mode) => {
        setQueueing(mode);
        setNotice(null);
        ownerResearchApi.runNow(mode).then(
            (data) => {
                if (unmounted.current) return;
                setQueueing(null);
                setNotice(data.queued
                    ? { kind: 'ok', text: mode === 'dry_run' ? 'Dry run queued — nothing will be written.' : 'Queued — starting within a minute.' }
                    : { kind: 'warn', text: data.reason });
                load();
                loadRuns(1);
            },
            (failure) => {
                if (unmounted.current) return;
                setQueueing(null);
                setNotice({ kind: 'error', text: failure.message || 'Could not start a run.' });
            }
        );
    };

    const saveSettings = (settings) => ownerResearchApi.saveSettings(settings).then((data) => {
        setStatus(data);
        return data;
    });

    const closeSettings = useCallback(() => setSettingsOpen(false), []);
    const closeReview = useCallback(() => setOpenReview(null), []);

    const resolved = (message) => {
        setOpenReview(null);
        setNotice({ kind: 'ok', text: message });
        setDetailVersion((version) => version + 1);
        load();
        loadReviews(reviewsPage);
    };

    if (error) {
        return (
            <div className="ListingSources OwnerResearch">
                <div className="ls-blockedwrap">
                    <div className="ls-blocked">
                        <div className="ls-blocked__mark" />
                        <div className="ls-blocked__title">Nothing to show</div>
                        <p className="ls-blocked__text">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!status) {
        return (
            <div className="ListingSources OwnerResearch">
                <div className="ls-container"><div className="ls-empty">Loading owner research…</div></div>
            </div>
        );
    }

    const run = status.run;
    const busy = status.running || queueing !== null;
    const zone = zoneAbbreviation(status.settings.timezone);
    const reviewTotal = reviewsMeta ? reviewsMeta.total : reviews.length;

    // The menu offers the member's own territories only when they have some; every other agent's, as "Agent · Territory"; and everything.
    const mine = Boolean(status.me && status.me.has_territories);
    const agents = (status.agents || []).filter((agent) => !(mine && agent.id === status.me.id));
    const pairOptions = agents.reduce((options, agent) => options.concat(
        agent.territories.map((territory) => [pairKey(agent.id, territory.id), agent.name + ' · ' + territory.name]),
    ), []);
    const named = appliedFilter || {};
    // A URL naming the member's own id is their own territories too.
    const asksMine = reviewFilter.agent === MINE || (mine && reviewFilter.agent === status.me.id && !reviewFilter.territory);
    // An agent with a single territory is that territory, however the URL names it.
    const soleTerritory = agents.filter((agent) => agent.id === reviewFilter.agent && agent.territories.length === 1)
        .map((agent) => agent.territories[0].id)[0];
    const territoryAsked = reviewFilter.territory || soleTerritory;
    const asked = reviewFilter.agent && territoryAsked && !asksMine && !reviewFilter.market ? pairKey(reviewFilter.agent, territoryAsked) : null;
    const listed = pairOptions.some((option) => option[0] === asked);
    const fromList = !listed && Boolean(reviewFilter.territory || reviewFilter.market);
    const fromListLabel = [
        named.agent ? named.agent.name : null,
        named.territories && named.territories.length ? named.territories.join(', ') : (reviewFilter.territory ? 'Territory' : 'Market') + ' from the property list',
    ].filter(Boolean).join(' · ');
    const filterValue = fromList
        ? FROM_LIST
        : listed
            ? asked
            : asksMine
                ? (mine ? MINE : ALL)
                : (reviewFilter.agent || ALL);
    // An agent the menu does not list (no territories any more, or several of them) is still named, from the server's answer.
    const unlisted = !fromList && !listed && filterValue !== MINE && filterValue !== ALL;
    const filterOptions = [
        mine ? [MINE, 'My territories'] : null,
        [ALL, 'All territories'],
        ...pairOptions,
        unlisted ? [filterValue, named.agent ? named.agent.name : 'Agent ' + filterValue] : null,
        fromList ? [FROM_LIST, fromListLabel] : null,
    ].filter(Boolean);
    const filtered = filterValue !== ALL;

    return (
        <div className="ListingSources OwnerResearch">
            <div className="ls-container">
                {!openRun && !showRuns && (
                    <div className="ls-header">
                        <div className="ls-header__headings">
                            <h1 className="ls-header__title">Owner Research Review</h1>
                            <p className="or-description">
                                Help the BOT make better decisions by using human logic to select the correct Investor for the following properties
                            </p>
                        </div>
                        <div className="ls-header__actions">
                            <button type="button" className="ls-btn ls-btn--quiet" onClick={() => setShowRuns(true)}>
                                Runs &amp; settings
                                <Chevron />
                            </button>
                        </div>
                    </div>
                )}

                {!openRun && showRuns && (
                    <button type="button" className="ls-backlink" onClick={() => setShowRuns(false)}>
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                            <path d="M8.4 2.8 L4.4 6.5 L8.4 10.2" />
                        </svg>
                        Review
                    </button>
                )}

                {!openRun && showRuns && (
                    <div className="ls-header">
                        <div className="ls-header__headings">
                            <h1 className="ls-header__title">Owner Research</h1>
                            <div className="ls-header__meta">
                                <span className={'ls-pill ls-pill--schedule' + (status.settings.enabled ? ' is-on' : '')}>
                                    {status.settings.enabled ? 'Schedule on' : 'Schedule off'}
                                </span>
                                <span className="ls-header__schedule">{scheduleLine(status)}</span>
                            </div>
                        </div>
                        <div className="ls-header__actions">
                            <button type="button" className="ls-btn" onClick={() => setSettingsOpen(true)}>
                                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                                    <circle cx="7.5" cy="7.5" r="2.2" />
                                    <path d="M7.5 1.6 V3.2 M7.5 11.8 V13.4 M1.6 7.5 H3.2 M11.8 7.5 H13.4 M3.4 3.4 L4.5 4.5 M10.5 10.5 L11.6 11.6 M11.6 3.4 L10.5 4.5 M4.5 10.5 L3.4 11.6" />
                                </svg>
                                Settings
                            </button>
                            <button type="button" className="ls-btn" disabled={busy} onClick={() => runNow('dry_run')}>
                                {queueing === 'dry_run' ? 'Queueing…' : 'Dry run'}
                            </button>
                            <button type="button" className="ls-btn ls-btn--primary" disabled={busy} onClick={() => runNow('write')}>
                                {status.running ? 'Run in progress' : queueing === 'write' ? 'Queueing…' : 'Run now'}
                            </button>
                        </div>
                    </div>
                )}

                {notice && (
                    <div className={'ls-alert ls-alert--' + notice.kind}>
                        <span className="ls-alert__text">{notice.text}</span>
                        <button type="button" className="ls-alert__dismiss" aria-label="Dismiss" onClick={() => setNotice(null)}>×</button>
                    </div>
                )}

                {openRun ? (
                    // The run as the last poll has it: the one caught at the click would never show a wait ending or a failure landing.
                    <RunDetail
                        run={runs.find((each) => each.id === openRun.id) || (status.run && status.run.id === openRun.id ? status.run : openRun)}
                        onBack={() => setOpenRun(null)}
                        onOpenReview={setOpenReview}
                        version={detailVersion}
                    />
                ) : !showRuns ? (
                    <div className="ls-overview">
                        <div className="ls-sectionhead">
                            <h2 className="ls-sectionhead__title">
                                Properties needing review<span className="or-count">{formatNumber(reviewTotal)}</span>
                            </h2>
                            <div className="or-reviewfilter">
                                <Menu value={filterValue} options={filterOptions} onPick={pickReviewFilter} />
                            </div>
                        </div>

                        <div className="ls-card">
                            {sectionError.reviews ? (
                                <div className="ls-empty">{sectionError.reviews}</div>
                            ) : reviews.length === 0 ? (
                                <div className="ls-empty">{filtered ? 'Nothing is waiting for a person in these territories' : 'Nothing is waiting for a person'}</div>
                            ) : (
                                <div>
                                    <div className="ls-table__scroll">
                                        <div className="ls-table ls-table--queue">
                                            <div className="ls-table__head">
                                                <div>Property</div>
                                                <div />
                                            </div>
                                            {reviews.map((review) => {
                                                const property = review.property || {};
                                                const place = [property.city, [property.state, property.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

                                                return (
                                                    <div
                                                        key={review.id}
                                                        className="ls-table__row"
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => setOpenReview(review)}
                                                        onKeyPress={(event) => event.key === 'Enter' && setOpenReview(review)}
                                                    >
                                                        <div>
                                                            <div className="ls-table__primary">{property.address}</div>
                                                            <div className="ls-table__sub">{place} · <span className="ls-mono">#{property.id}</span></div>
                                                        </div>
                                                        <div className="ls-table__chevron"><Chevron /></div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="ls-pager">
                                        <span className="ls-pager__label">{formatRange(reviewsMeta, reviews.length)}</span>
                                        <button type="button" className="ls-btn ls-btn--small" disabled={reviewsPage <= 1} onClick={() => loadReviews(reviewsPage - 1)}>
                                            Previous
                                        </button>
                                        <button
                                            type="button"
                                            className="ls-btn ls-btn--small"
                                            disabled={!reviewsMeta || reviewsPage >= reviewsMeta.last_page}
                                            onClick={() => loadReviews(reviewsPage + 1)}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="ls-overview">
                        {!run ? (
                            <div className="ls-card ls-hero ls-hero--empty">
                                <div className="ls-hero__kicker">No runs yet</div>
                                <p className="ls-hero__empty">
                                    Start with a dry run to see what it would do, or turn the nightly run on in Settings.
                                </p>
                                <Backlog status={status} />
                            </div>
                        ) : (
                            <div className="ls-card ls-hero">
                                {run.mode === 'dry_run' && (
                                    <div className="ls-hero__dryband">
                                        <span className="ls-hero__dryband-tag">Dry run</span>
                                        <span>Nothing was written</span>
                                    </div>
                                )}
                                <div className="ls-hero__head">
                                    <div className="ls-hero__headings">
                                        <div className="ls-hero__kicker">{run.status === 'running' ? 'Running now' : 'Last run'}</div>
                                        <div className="ls-hero__meta">
                                            <StatusPill status={run.status} />
                                            <ModePill mode={run.mode} />
                                            <span className="ls-hero__time">
                                                {formatRunTime(run.started_at)} {zone}
                                                {run.finished_at ? ' · finished ' + formatClock(run.finished_at) : ''}
                                                {' · ' + run.started_by}
                                            </span>
                                        </div>
                                    </div>
                                    <button type="button" className="ls-btn ls-btn--quiet" onClick={() => setOpenRun(run)}>
                                        {run.status === 'running' ? 'Watch run' : 'Open run'}
                                        <Chevron />
                                    </button>
                                </div>
                                <CountChips run={run} large />
                                <WaitingStrip run={run} />
                                {run.error && (
                                    <div className="ls-alert ls-alert--error ls-hero__error">
                                        <span className="ls-alert__mark">!</span>
                                        <span className="ls-alert__text">{run.error}</span>
                                        <button type="button" className="ls-alert__retry" disabled={busy} onClick={() => runNow(run.mode)}>
                                            Run again
                                        </button>
                                    </div>
                                )}
                                <Backlog status={status} />
                            </div>
                        )}

                        <div className="ls-sectionhead">
                            <h2 className="ls-sectionhead__title">Run history</h2>
                            <div className="ls-search">
                                <svg width="15" height="15" viewBox="0 0 14 14" fill="none" stroke="#7A8698" strokeWidth="1.6" aria-hidden="true">
                                    <circle cx="6" cy="6" r="4.4" />
                                    <path d="M9.4 9.4 L12.6 12.6" />
                                </svg>
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Property id"
                                    aria-label="Find what the runs did to a property, by its id"
                                />
                                {query && (
                                    <button type="button" className="ls-search__clear" aria-label="Clear" onClick={() => setQuery('')}>×</button>
                                )}
                            </div>
                        </div>

                        {sectionError.search && query.trim() && (
                            <div className="ls-card ls-search__panel">
                                <div className="ls-search__headline">{sectionError.search}</div>
                            </div>
                        )}

                        {results && (
                            <div className="ls-card ls-search__panel">
                                <div className="ls-search__headline">
                                    {results.length === 0 ? 'No run has looked at property ' + query.trim() : results.length + ' answers for property ' + query.trim()}
                                </div>
                                {results.map((hit) => (
                                    <button key={hit.id} type="button" className="ls-search__hit" onClick={() => setOpenRun(runs.filter((each) => each.id === hit.run.id)[0] || { id: hit.run.id, mode: hit.run.mode, started_at: hit.run.started_at })}>
                                        <span className="ls-mono ls-search__id">#{hit.external_id}</span>
                                        <OutcomePill outcome={hit.outcome} />
                                        <span className="ls-search__reason">{hit.reason}</span>
                                        <span className="ls-search__where">run #{hit.run.id} · {formatRunTime(hit.run.started_at)}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="ls-card">
                            {sectionError.runs ? (
                                <div className="ls-empty">{sectionError.runs}</div>
                            ) : runs.length === 0 ? (
                                <div className="ls-empty">No runs recorded yet.</div>
                            ) : (
                                <div>
                                    <div className="ls-table__scroll">
                                        <div className="ls-table ls-table--runs">
                                            <div className="ls-table__head">
                                                <div>Run</div>
                                                <div>Status</div>
                                                <div>Mode</div>
                                                <div className="is-numeric">Linked</div>
                                                <div className="is-numeric">New investor</div>
                                                <div className="is-numeric">Review</div>
                                                <div className="is-numeric">Left alone</div>
                                                <div className="is-numeric">Failed</div>
                                                <div>Started by</div>
                                                <div />
                                            </div>
                                            {runs.map((each) => (
                                                <div
                                                    key={each.id}
                                                    className={'ls-table__row' + (each.status === 'running' ? ' is-running' : '')}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setOpenRun(each)}
                                                    onKeyPress={(event) => event.key === 'Enter' && setOpenRun(each)}
                                                >
                                                    <div>
                                                        <div className="ls-table__primary">{formatRunTime(each.started_at)}</div>
                                                        <div className="ls-mono ls-table__sub">run #{each.id}</div>
                                                    </div>
                                                    <div><StatusPill status={each.status} cell /></div>
                                                    <div><ModePill mode={each.mode} cell /></div>
                                                    {OUTCOMES.map((key) => (
                                                        <div
                                                            key={key}
                                                            className={[
                                                                'ls-mono is-numeric',
                                                                each[key] ? '' : 'is-zero',
                                                                key === 'failed' && each[key] ? 'is-bad' : '',
                                                                each.mode === 'dry_run' && each[key] ? 'is-dry' : '',
                                                            ].filter(Boolean).join(' ')}
                                                        >
                                                            {formatNumber(each[key])}
                                                        </div>
                                                    ))}
                                                    <div className="ls-table__by">{each.started_by}</div>
                                                    <div className="ls-table__chevron"><Chevron /></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="ls-pager">
                                        <span className="ls-pager__label">{formatRange(runsMeta, runs.length)}</span>
                                        <button type="button" className="ls-btn ls-btn--small" disabled={runsPage <= 1} onClick={() => loadRuns(runsPage - 1)}>
                                            Newer
                                        </button>
                                        <button
                                            type="button"
                                            className="ls-btn ls-btn--small"
                                            disabled={!runsMeta || runsPage >= runsMeta.last_page}
                                            onClick={() => loadRuns(runsPage + 1)}
                                        >
                                            Older
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {settingsOpen && (
                <SettingsModal
                    settings={status.settings}
                    statuses={status.statuses}
                    onSave={saveSettings}
                    onClose={closeSettings}
                />
            )}

            {openReview && <ReviewWindow item={openReview} onClose={closeReview} onResolved={resolved} />}
        </div>
    );
}
